/**
 * Formatting one API money amount for display.
 *
 * Amounts stay as strings in transport and storage. This helper only converts
 * one value at the final display boundary; malformed amounts or currency codes
 * fall back to their recorded text rather than breaking the screen.
 */
export function formatMoney(value: string, currency: string): string {
  const amount = Number(value);
  const normalizedCurrency = currency.toUpperCase();
  if (!Number.isFinite(amount)) return `${normalizedCurrency} ${value}`;

  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: normalizedCurrency,
    }).format(amount);
  } catch {
    return `${normalizedCurrency} ${value}`;
  }
}

/**
 * Adding up money that arrived as text.
 *
 * FastAPI sends amounts as strings — "465.00", not 465 — and the front end
 * keeps them that way, because turning them into floats on the way to a screen
 * is how a penny goes missing. A per-tutor table wants a total under it, and a
 * total is the one place that rule has to be bent, so it is bent exactly here
 * and in whole pence rather than anywhere in a component.
 *
 * Anything that is not plainly an amount makes the whole total `null` and the
 * row is left off the table. A sum that silently skipped a value it could not
 * read would be a smaller number presented as the full one, which is worse than
 * no number at all under a column somebody is about to add up by hand.
 */

const AMOUNT = /^(-?)(\d+)(?:\.(\d{1,2}))?$/;

/** One amount in whole pence, or null when it is not an amount. */
export function pence(value: string): number | null {
  const match = AMOUNT.exec(value.trim());
  if (!match) return null;

  const [, sign, whole, fraction = ""] = match;
  const total = Number(whole) * 100 + Number(fraction.padEnd(2, "0"));
  return Number.isSafeInteger(total) ? (sign === "-" ? -total : total) : null;
}

/**
 * A column of amounts, added.
 *
 * Null for an empty list as well as for an unreadable one: there is no total to
 * print under no rows, and printing "0.00" there would read as "they took
 * nothing" rather than "nobody was asked".
 */
export function sumMoney(values: readonly string[]): string | null {
  if (values.length === 0) return null;

  let total = 0;
  for (const value of values) {
    const amount = pence(value);
    if (amount === null) return null;
    total += amount;
  }

  const sign = total < 0 ? "-" : "";
  const absolute = Math.abs(total);
  return `${sign}${Math.floor(absolute / 100)}.${String(absolute % 100).padStart(2, "0")}`;
}
